/**
 * Cloud Tenant App — Composes @ensafely/checker-ui pages with cloud-specific
 * routes, auth, and customization.
 *
 * Instead of build-time patching, cloud functionality is added through:
 * - Module alias overrides (api.ts) for Bearer token auth
 * - WebSocketConfigProvider for JWT auth on WebSocket connections
 * - Component composition props (extraNavItems, extraColumns, etc.)
 * - Additional cloud routes (billing, team, account, etc.)
 *
 * The shared UI library's pages (Dashboard, Management, Alerts, Settings)
 * are used directly with customization props. Cloud-specific pages are
 * added as extra routes.
 */
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom'
import {
  Dashboard,
  Management,
  Alerts,
  Settings,
  CommandPalette,
  TopBarConfigProvider,
  Logo,
  TestCooldownProvider,
  WebSocketConfigProvider,
  useTheme,
  Toaster,
  type ConfigField,
  AppErrorBoundary,
} from '@ensafely/checker-ui'

// TopBar config (nav items + user dropdown items) lives in ./topBarConfig
// for test-friendly importability.
import { CLOUD_NAV_ITEMS, CLOUD_USER_MENU_ITEMS } from './topBarConfig'

// Cloud-specific pages
import LoginPage from './pages/LoginPage'
import SignupPage from './pages/SignupPage'
import AccountPage from './pages/AccountPage'
import BillingPage from './pages/BillingPage'
import TeamPage from './pages/TeamPage'
import VerifyEmailPage from './pages/VerifyEmailPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import AcceptInvitePage from './pages/AcceptInvitePage'
import APIKeysPage from './pages/APIKeysPage'
import ConnectionsPage from './pages/ConnectionsPage'
import EscalationPoliciesPage from './pages/EscalationPoliciesPage'
import EdgeInstancesPage from './pages/EdgeInstancesPage'
import EdgeProbesPage from './pages/EdgeProbesPage'
import PlatformRegionsPage from './pages/PlatformRegionsPage'
import StatusPageConfig from './pages/StatusPageConfig'
import ChoosePlanPage from './pages/ChoosePlanPage'
import ReportsPage from './pages/ReportsPage'

// Cloud components
import { createStatusPageSection } from './components/StatusPageToggle'
import RequireAuth from './components/RequireAuth'
import { TenantStatusProvider } from './components/TenantStatusContext'
import { SuspendedBanner } from './components/SuspendedBanner'
import { EmailVerificationBanner } from './components/EmailVerificationBanner'
import { UserSuspendedProvider } from './components/UserSuspendedContext'
import { UserSuspendedBanner } from './components/UserSuspendedBanner'
import { PaddleProvider } from './components/PaddleProvider'
import { VersionBadge } from './components/VersionBadge'
import { clearToken, getToken } from './lib/authToken'

/**
 * Cloud uses Resend for email delivery — tenants only need recipient addresses.
 * Hide SMTP configuration fields that are irrelevant in the managed environment.
 */
const CLOUD_CONFIG_FIELDS_OVERRIDE: Partial<Record<string, ConfigField[]>> = {
  email: [
    { key: 'to', label: 'To Addresses', type: 'tags', placeholder: 'recipient@example.com', required: true },
  ],
}

/** Status page toggle section for the check edit drawer */
const statusPageExtraSection = createStatusPageSection()

function AppContent() {
  const { resolved } = useTheme()
  const navigate = useNavigate()

  const handleLogout = () => {
    clearToken()
    navigate('/login')
  }

  return (
    <TopBarConfigProvider
      brandName="Ensafely"
      logo={<Logo variant="horizontal" className="h-7 w-auto" />}
      extraNavItems={CLOUD_NAV_ITEMS}
      userMenuItems={CLOUD_USER_MENU_ITEMS}
      onLogout={handleLogout}
    >
      <SuspendedBanner />
      <UserSuspendedBanner />
      <EmailVerificationBanner />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />

        {/* Post-signup plan selection (protected) */}
        <Route path="/choose-plan" element={<RequireAuth><ChoosePlanPage /></RequireAuth>} />

        {/* Checker routes (protected) — composed from @ensafely/checker-ui */}
        <Route path="/" element={<RequireAuth><Dashboard /></RequireAuth>} />
        <Route path="/manage" element={<RequireAuth><Management checkEditExtraSections={statusPageExtraSection} /></RequireAuth>} />
        <Route path="/alerts" element={<RequireAuth><Alerts /></RequireAuth>} />
        <Route path="/settings" element={<RequireAuth><Settings configFieldsOverride={CLOUD_CONFIG_FIELDS_OVERRIDE} /></RequireAuth>} />

        {/* Cloud SaaS routes (protected) */}
        <Route path="/account" element={<RequireAuth><AccountPage /></RequireAuth>} />
        <Route path="/account/billing" element={<RequireAuth><BillingPage /></RequireAuth>} />
        <Route path="/account/team" element={<RequireAuth><TeamPage /></RequireAuth>} />
        <Route path="/account/api-keys" element={<RequireAuth><APIKeysPage /></RequireAuth>} />
        <Route path="/account/edge-instances" element={<RequireAuth><EdgeInstancesPage /></RequireAuth>} />
        <Route path="/account/edge-probes" element={<RequireAuth><EdgeProbesPage /></RequireAuth>} />
        <Route path="/account/platform-regions" element={<RequireAuth><PlatformRegionsPage /></RequireAuth>} />
        <Route path="/account/status-page" element={<RequireAuth><StatusPageConfig /></RequireAuth>} />
        <Route path="/connections" element={<RequireAuth><ConnectionsPage /></RequireAuth>} />
        <Route path="/escalation-policies" element={<RequireAuth><EscalationPoliciesPage /></RequireAuth>} />
        <Route path="/reports" element={<RequireAuth><ReportsPage /></RequireAuth>} />
      </Routes>
      <CommandPalette />
      <VersionBadge />
      <Toaster theme={resolved} richColors position="bottom-right" />
    </TopBarConfigProvider>
  )
}

const TENANT_WS_CONFIG = {
  getAuthToken: () => getToken(),
}

export function App() {
  return (
    <AppErrorBoundary>
      <BrowserRouter>
        <WebSocketConfigProvider config={TENANT_WS_CONFIG}>
          <TestCooldownProvider>
            <TenantStatusProvider>
              <UserSuspendedProvider>
                <PaddleProvider>
                  <AppContent />
                </PaddleProvider>
              </UserSuspendedProvider>
            </TenantStatusProvider>
          </TestCooldownProvider>
        </WebSocketConfigProvider>
      </BrowserRouter>
    </AppErrorBoundary>
  )
}
